import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  useTheme,
  useMediaQuery,
  Stack,
  Avatar,
  Chip,
  Fade,
  Skeleton,
  TablePagination,
  TextField,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import HotelIcon from '@mui/icons-material/Hotel';
import config from '../../Config';

export default function UserReservations() {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [propertyFilter, setPropertyFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');

  const [isFiltering, setIsFiltering] = useState(false);

  const LIMIT = 20;

  const fetchReservations = async (
    pageNumber = 1,
    filters = { status: '', property: '', search: '', filtering: false }
  ) => {
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      let url = '';

      if (filters.filtering) {
        url = `${config.apiBaseUrl}/admin/reservation-filter?status=${filters.status}&propertyName=${filters.property}&search=${filters.search}&page=${pageNumber}&limit=${LIMIT}`;
      } else {
        url = `${config.apiBaseUrl}/admin/reservation/getAll?page=${pageNumber}&limit=${LIMIT}`;
      }

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (response.ok && data.bookings) {
        setReservations(data.bookings);
        setTotalCount(data.total || 0);
      } else {
        setReservations([]);
        setTotalCount(0);
      }
    } catch (error) {
      setReservations([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReservations(1, { filtering: false });
  }, []);

  const applyFilters = () => {
    setIsFiltering(true);
    setPage(0);

    fetchReservations(1, {
      status: statusFilter,
      property: propertyFilter,
      search: searchFilter,
      filtering: true,
    });
  };

  const clearFilters = () => {
    setStatusFilter('');
    setPropertyFilter('');
    setSearchFilter('');

    setIsFiltering(false);
    setPage(0);

    fetchReservations(1, { filtering: false });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);

    fetchReservations(newPage + 1, {
      status: statusFilter,
      property: propertyFilter,
      search: searchFilter,
      filtering: isFiltering,
    });
  };

  const formatDate = (dateStr) =>
    new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmed':
        return 'success';
      case 'not_confirmed':
        return 'warning';
      case 'cancelled':
        return 'error';
      case 'completed':
        return 'info';
      case 'checked_out':
        return 'info';
      default:
        return 'default';
    }
  };

  const renderLoadingSkeleton = () => (
    <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
      <Skeleton variant="text" width={200} height={32} sx={{ mb: 2 }} />
      <Stack spacing={2}>
        {[...Array(7)].map((_, i) => (
          <Skeleton key={i} variant="rectangular" height={60} sx={{ borderRadius: 1 }} />
        ))}
      </Stack>
    </Paper>
  );

  const renderTable = () => (
    <Fade in={!loading}>
      <Paper
        elevation={2}
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        }}
      >
        <Box sx={{ p: 3, background: 'white' }}>
          {/* FILTERS */}
          <Stack direction="row" spacing={2} mb={3} flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                renderValue={(selected) => (selected === '' ? 'All' : selected)}
              >
                {/* <MenuItem value="">All</MenuItem> */}
                <MenuItem value="confirmed">Confirmed</MenuItem>
                <MenuItem value="not_confirmed">Not Confirmed</MenuItem>
                <MenuItem value="checked_out">Checked Out</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Property</InputLabel>
              <Select
                label="Property"
                value={propertyFilter}
                onChange={(e) => setPropertyFilter(e.target.value)}
                renderValue={(selected) => (selected === '' ? 'All' : selected)}
              >
                {/* <MenuItem value="">All</MenuItem> */}
                <MenuItem value="Homesly Guest Rooms">Homesly Guest Rooms</MenuItem>
                <MenuItem value="Hull Guest House">Hull Guest House</MenuItem>
                <MenuItem value="The Coach House">The Coach House</MenuItem>
                <MenuItem value="The County Hotel">The County Hotel</MenuItem>
                <MenuItem value="The George Hotel">The George Hotel</MenuItem>
                <MenuItem value="The Station Hotel">The Station Hotel</MenuItem>
                <MenuItem value="Woodman Inn">Woodman Inn</MenuItem>
              </Select>
            </FormControl>

            {/* SEARCH INPUT */}
            <TextField
              label="Search"
              size="small"
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
            />

            <Button variant="contained" onClick={applyFilters}>
              Apply
            </Button>

            <Button variant="outlined" color="error" onClick={clearFilters}>
              Clear
            </Button>
          </Stack>

          {/* HEADER */}
          <Stack direction="row" alignItems="center" spacing={2} mb={3}>
            <HotelIcon color="primary" />
            <Typography variant="h5" fontWeight="bold" color="primary.main">
              Reservation Management
            </Typography>
            <Chip label={`${totalCount} Total`} color="primary" variant="outlined" size="small" />
          </Stack>

          {/* TABLE */}
          <Box sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    Reservation ID
                  </TableCell>

                  <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    Property Name
                  </TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    Guest Details
                  </TableCell>

                  <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    Booking Date
                  </TableCell>

                  <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>Status</TableCell>

                  <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    Created At
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {reservations.map((res) => (
                  <TableRow
                    key={res._id}
                    sx={{
                      '&:hover': {
                        backgroundColor: '#f1f5f9',
                        transform: 'scale(1.01)',
                        transition: 'all 0.2s',
                      },
                    }}
                  >
                    <TableCell>
                      <Typography
                        variant="body2"
                        fontWeight="bold"
                        color="primary"
                        sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={() => navigate(`/reservationdetail/${res.reservationID}`)}
                      >
                        {res.reservationID}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {res.propertyName || '-'}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                          <PersonIcon fontSize="small" />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {res.guestName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {res.guestEmail}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>

                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {/* Left: Centered Calendar Icon */}
                        <CalendarTodayIcon fontSize="small" sx={{ color: 'text.secondary' }} />

                        {/* Right: Dates (Top & Bottom) */}
                        <Stack spacing={0}>
                          <Typography variant="body2" fontSize="12px">
                            {formatDate(res.startDate)}
                          </Typography>
                          <Typography variant="body2" fontSize="12px" color="text.secondary">
                            {formatDate(res.endDate)}
                          </Typography>
                        </Stack>
                      </Stack>
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={res.status}
                        color={getStatusColor(res.status)}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>

                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(res.createdAt)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}

                {reservations.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <Stack alignItems="center" spacing={2}>
                        <HotelIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                        <Typography variant="h6" color="text.secondary">
                          No reservations found
                        </Typography>
                        <Typography variant="body2" color="text.disabled">
                          Try adjusting your filters
                        </Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={LIMIT}
            rowsPerPageOptions={[LIMIT]}
          />
        </Box>
      </Paper>
    </Fade>
  );

  return (
    <Box sx={{ px: 3, py: 2, minHeight: '100vh', bgcolor: '#f8fafc' }}>
      <Typography
        variant={isSmDown ? 'h5' : 'h4'}
        fontWeight="bold"
        mb={4}
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
        }}
      >
        User Reservations
      </Typography>

      {loading ? renderLoadingSkeleton() : renderTable()}
    </Box>
  );
}
